import React, { useState, useRef, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Search, ChevronDown, X, Check } from 'lucide-react';
import { useDropdownPortal, useOutsideClose } from './FilterBar';

/**
 * A single-choice dropdown with a search box — for any list long enough that
 * scrolling a native <select> is painful (employees, assets, companies…).
 *
 * The panel is portalled to <body>, so it is never clipped by a scrolling
 * modal or trapped behind a `.glass` card's backdrop-filter stacking context.
 * Type to filter, ↑/↓ to move, Enter to pick, Esc to close.
 *
 * options: [{ value, label, hint? }]  — `hint` is a dimmed second line
 */
export default function SearchableSelect({
  value, onChange, options = [], placeholder = 'Select...',
  required = false, disabled = false, className = '', style = {},
  searchPlaceholder = 'Type to search...',
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [cursor, setCursor] = useState(0);
  const ref = useRef(null);
  const panelRef = useRef(null);
  const listRef = useRef(null);

  const pos = useDropdownPortal(open, ref, 260);
  useOutsideClose(open, [ref, panelRef], () => setOpen(false));

  const selected = options.find(o => String(o.value) === String(value));

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return options;
    // Matches that start with the query come first — typing "ami" should put
    // "Amila" above "Benjamin", which a plain `includes` filter does not do.
    const starts = [], contains = [];
    for (const o of options) {
      const l = String(o.label || '').toLowerCase();
      const h = String(o.hint || '').toLowerCase();
      if (l.startsWith(q)) starts.push(o);
      else if (l.includes(q) || h.includes(q)) contains.push(o);
    }
    return [...starts, ...contains];
  }, [options, search]);

  useEffect(() => { setCursor(0); }, [search, open]);

  // Keep the highlighted row in view while arrowing through a long list
  useEffect(() => {
    if (!open || !listRef.current) return;
    const el = listRef.current.querySelector(`[data-idx="${cursor}"]`);
    if (el) el.scrollIntoView({ block: 'nearest' });
  }, [cursor, open]);

  const pick = (v) => { onChange(v); setOpen(false); setSearch(''); };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setCursor(c => Math.min(c + 1, filtered.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setCursor(c => Math.max(c - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); if (filtered[cursor]) pick(filtered[cursor].value); }
    else if (e.key === 'Escape') { e.preventDefault(); setOpen(false); }
    else e.stopPropagation();
  };

  const panel = open && pos && createPortal(
    <div ref={panelRef}
      style={{
        position: 'fixed', top: pos.top, left: pos.left, right: pos.right,
        minWidth: Math.max(pos.minWidth || 0, 240), zIndex: 10000,
        background: '#0d1528', border: '1px solid rgba(59,130,246,0.3)',
        borderRadius: 12, overflow: 'hidden', boxShadow: '0 8px 32px rgba(0,0,0,0.8)',
      }}>
      <div style={{ padding: 8, borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
        <div className="flex items-center gap-2 px-2 py-1.5 rounded-lg"
          style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
          <Search size={11} style={{ color: '#475569', flexShrink: 0 }}/>
          <input autoFocus type="text" value={search} placeholder={searchPlaceholder}
            onChange={e => setSearch(e.target.value)} onKeyDown={onKeyDown}
            className="bg-transparent text-xs outline-none w-full" style={{ color: '#e2e8f0' }}/>
          {search && (
            <span onMouseDown={e => { e.preventDefault(); setSearch(''); }} style={{ cursor: 'pointer', color: '#475569' }}>
              <X size={11}/>
            </span>
          )}
        </div>
        {options.length > 8 && (
          <div className="text-xs mt-1.5 px-1" style={{ color: '#334155', fontSize: 10 }}>
            {search ? `${filtered.length} of ${options.length}` : `${options.length} to choose from`} · ↑↓ to move, Enter to select
          </div>
        )}
      </div>

      <div ref={listRef} style={{ overflowY: 'auto', maxHeight: 240 }}>
        {!required && !search && (
          <div onMouseDown={() => pick('')}
            className="px-3 py-2 text-xs cursor-pointer"
            style={{ color: '#475569', borderBottom: '1px solid rgba(255,255,255,0.04)' }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(59,130,246,0.08)'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
            {placeholder}
          </div>
        )}
        {filtered.length === 0 ? (
          <div className="px-3 py-5 text-xs text-center" style={{ color: '#334155' }}>
            Nothing matches “{search}”
          </div>
        ) : filtered.map((opt, i) => {
          const isSel = String(opt.value) === String(value);
          const isCur = i === cursor;
          return (
            <div key={opt.value} data-idx={i}
              onMouseDown={() => pick(opt.value)}
              onMouseEnter={() => setCursor(i)}
              className="px-3 py-2 text-xs cursor-pointer flex items-center gap-2"
              style={{
                color: isSel ? '#60a5fa' : '#94a3b8',
                background: isCur ? 'rgba(59,130,246,0.12)' : isSel ? 'rgba(59,130,246,0.07)' : 'transparent',
              }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{opt.label}</div>
                {opt.hint && <div style={{ color: '#334155', fontSize: 10, marginTop: 1 }}>{opt.hint}</div>}
              </div>
              {isSel && <Check size={12} style={{ color: '#60a5fa', flexShrink: 0 }}/>}
            </div>
          );
        })}
      </div>
    </div>,
    document.body
  );

  return (
    <div ref={ref} className={`relative ${className}`} style={style}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => { setOpen(o => !o); setSearch(''); }}
        className="input-dark w-full flex items-center justify-between gap-2 text-left"
        style={{ cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.6 : 1 }}
      >
        <span style={{ color: selected ? '#e2e8f0' : '#475569', fontSize: 13, flex: 1, textAlign: 'left',
                       overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {selected ? selected.label : placeholder}
        </span>
        <div className="flex items-center gap-1 flex-shrink-0">
          {value && !required && (
            <span onMouseDown={e => { e.stopPropagation(); e.preventDefault(); onChange(''); }}
              style={{ color: '#475569', cursor: 'pointer' }}>
              <X size={12}/>
            </span>
          )}
          <ChevronDown size={13} style={{ color: '#475569', transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}/>
        </div>
      </button>
      {panel}
    </div>
  );
}
